import { createBrowserHistory as createHistory } from 'history';
import hypernova, { load, serialize } from 'hypernova';
import queryString from 'query-string';
import React, { ReactNode, createElement, useEffect } from 'react';
import { hydrate } from 'react-dom';
import { renderToStaticMarkup, renderToString } from 'react-dom/server';
import Loadable from 'react-loadable';
import { Provider } from 'react-redux';
import { Router, StaticRouter, useLocation } from 'react-router-dom';
import { Store } from 'redux';
import { ServerStyleSheet } from 'styled-components';

import { CurrentUser } from 'api/currentUser';
import WithConfig from 'containers/WithConfig';
import {
  bootstrapAnalytics,
  registerAbExperiments,
  trackEvent,
} from 'utils/analytics';
import configureTrackJs from 'utils/configureTrackJs';
import { initXMLHttpRequestActiveCounter } from 'utils/http';
import renderAmpPage from 'utils/renderAmpPage';
import { trackReferrals } from 'utils/trackReferral';

import { State as MainState } from '../redux/main';
import {
  setABExperimentBucket,
  updateCurrentUser,
} from '../redux/main/actions/currentUser';
import { State as StaffState } from '../redux/staff';

const performanceMark = (...args) => {
  if (
    window &&
    window.performance &&
    typeof window.performance.mark === 'function'
  ) {
    // @ts-expect-error ts-migrate(2556) FIXME: Expected 1 arguments, but got 0 or more.
    window.performance.mark(...args);
  }
};

const AppContainer = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
};

// Accept a query param to override an AB experiment bucket:
// ?ab_test[button_color]=red
const overrideABExperiments = (store, currentUser) => {
  const queryParams = queryString.parse(window.location.search);
  const overrides = Object.keys(queryParams).reduce((acc, param) => {
    const matches = param.match(/^ab_test\[([^\]]+)\]$/);

    if (matches) {
      acc.push({
        id: matches[1],
        bucket: queryParams[param],
      });
    }

    return acc;
  }, []);
  overrides.forEach(({ id, bucket }) => {
    const experiment = currentUser.abExperiments.find((e) => e.id === id);

    if (!experiment || experiment.bucket !== bucket) {
      store.dispatch(setABExperimentBucket(id, bucket));
    }
  });
};

const login = (store): Promise<CurrentUser> => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line prefer-destructuring
  let currentUser = store.getState().currentUser;

  if (currentUser && currentUser.isLoaded) {
    return Promise.resolve(currentUser);
  }

  const userPromise = new Promise((resolve) => {
    // Instead of fetching the user here, we're using making the request in the
    // HTML template directly. Otherwise, the response from the Link HTTP header is
    // not used and we're making a second request.
    const interval = setInterval(() => {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'initUser' does not exist on type '{ page... Remove this comment to see the full error message
      if (window.Idealist.initUser) {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'initUser' does not exist on type '{ page... Remove this comment to see the full error message
        resolve(window.Idealist.initUser);
        clearInterval(interval);
      }
    }, 50);
  });
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  return userPromise.then((fetchedUser) => {
    // FIXME: refactor redux store to get rid of this workaround
    // This fixes a race condition where the user info is being fetched while the
    // search location is being set
    currentUser = store.getState().currentUser;

    if (!currentUser.isLoaded && currentUser.searchLocation) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'searchLocation' does not exist on type '... Remove this comment to see the full error message
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-param-reassign
      fetchedUser.searchLocation = currentUser.searchLocation;
    }

    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    store.dispatch(updateCurrentUser(fetchedUser));
    return fetchedUser;
  });
};

const renderReact = (
  name: string,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>,
  createStore: (preloadedState?: MainState | StaffState) => Store
) =>
  hypernova({
    server() {
      return Loadable.preloadAll().then(() => (props) => {
        const componentWrapper = ({
          url,
          state,
          requestIsAmp,
          config,
        }: {
          url: string;
          state: MainState | StaffState;
          requestIsAmp: boolean;
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          config: Record<string, any>;
        }) => {
          const store = createStore(state);
          return (
            <Provider store={store}>
              {/* @ts-expect-error ts-migrate(2786) FIXME: 'WithConfig' cannot be used as a JSX component. */}
              <WithConfig config={config}>
                <StaticRouter location={url} context={{}}>
                  <Component requestIsAmp={requestIsAmp} />
                </StaticRouter>
              </WithConfig>
            </Provider>
          );
        };

        const sheet = new ServerStyleSheet();
        const element = createElement(componentWrapper, props);

        if (props.requestIsAmp) {
          const {
            title,
            description,
            canonicalUrl,
            altUrls,
            ampTrackingId,
            ampSchemaMarkup,
          } = props;
          const body = renderToStaticMarkup(
            sheet.collectStyles(element)
          ).replace(/data-style/g, 'style');
          const styleTags = sheet
            .getStyleTags()
            .replace(/<\/?style[^>]*>/g, '')
            .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
          return renderAmpPage({
            title,
            description,
            styles: styleTags,
            body,
            canonicalUrl,
            altUrls,
            ampTrackingId,
            ampSchemaMarkup,
          });
        }

        const html = renderToString(sheet.collectStyles(element));
        const styleTags = sheet
          .getStyleTags()
          .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
        const markup = serialize(name, html, {
          config: props.config,
          state: props.state,
        });
        return `${styleTags}\n${markup}`;
      });
    },

    client() {
      performanceMark('Starting app');
      const browserHistory = createHistory();
      // Track js "pageViews" using GTM
      browserHistory.listen((location, action) => {
        if (action !== 'REPLACE') {
          trackEvent('JS Page View');
        }
      });

      // @ts-expect-error ts-migrate(2339) FIXME: Property 'debugXhr' does not exist on type '{ page... Remove this comment to see the full error message
      if (window.Idealist && window.Idealist.debugXhr) {
        initXMLHttpRequestActiveCounter();
      }

      const payloads = load(name);

      if (payloads.length > 0) {
        const payload = payloads[0];
        const {
          node,
          data: { state, config },
        } = payload;

        if (node) {
          Loadable.preloadReady().then(() => {
            const store = createStore(state);
            login(store).then((currentUser) => {
              configureTrackJs(currentUser.id);
              bootstrapAnalytics({
                user: currentUser,
              });
              trackReferrals();
              store.subscribe(() => {
                registerAbExperiments(
                  store.getState().currentUser.abExperiments
                );
              });
              overrideABExperiments(store, currentUser);
              performanceMark('User data first load');
            });
            hydrate(
              <Provider store={store}>
                {/* @ts-expect-error ts-migrate(2786) FIXME: 'WithConfig' cannot be used as a JSX component. */}
                <WithConfig config={config}>
                  <Router history={browserHistory}>
                    {/* @ts-expect-error ts-migrate(2786) FIXME: 'AppContainer' cannot be used as a JSX component. */}
                    <AppContainer>
                      <Component />
                    </AppContainer>
                  </Router>
                </WithConfig>
              </Provider>,
              node,
              () => performanceMark('App ready')
            );
          });
        }
      }
    },
  });

export default renderReact;
